import React, { useState } from "react";
import db from "../utils/db";

const DataExport = () => {
  const [showDataModal, setShowDataModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [timeFormat, setTimeFormat] = useState(
    localStorage.getItem("timeFormat") || "24h"
  );

  const handleExport = async () => {
    try {
      const data = await db.exportData();

      if (exportFormat === "csv") {
        // Convert to CSV format
        const csvContent = convertToCSV(data);

        // Create and download file
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `productivity-timer-data-${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert("Data exported successfully as CSV!");
      } else if (exportFormat === "json") {
        // Export as JSON
        const jsonContent = JSON.stringify(data, null, 2);

        const blob = new Blob([jsonContent], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `productivity-timer-data-${
          new Date().toISOString().split("T")[0]
        }.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert("Data exported successfully as JSON!");
      }
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data. Please try again.");
    }
  };

  const convertToCSV = (data) => {
    const headers = [
      "Project",
      "Task Title",
      "Description",
      "Category",
      "Status",
      "Time Tracked (seconds)",
      "Time Tracked (formatted)",
      "Created Date",
      "Updated Date",
    ];

    const rows = data.tasks.map((task) => {
      const project = data.projects.find((p) => p.id === task.projectId);
      const projectName = project ? project.name : "Unknown Project";

      const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours}h ${minutes}m ${secs}s`;
      };

      return [
        projectName,
        task.title,
        task.description.replace(/\n/g, " "), // Replace newlines with spaces
        task.category,
        task.status,
        task.timeTracked,
        formatTime(task.timeTracked),
        new Date(task.createdAt).toLocaleDateString(),
        new Date(task.updatedAt).toLocaleDateString(),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((field) => `"${field}"`).join(",")),
    ].join("\n");

    return csvContent;
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      let data;

      // Determine file type and parse accordingly
      if (file.name.endsWith(".json")) {
        data = JSON.parse(text);
      } else if (file.name.endsWith(".csv")) {
        data = parseCSV(text);
      } else {
        throw new Error(
          "Unsupported file format. Please use CSV or JSON files."
        );
      }

      if (
        window.confirm(
          `Import ${data.tasks?.length || 0} tasks and ${
            data.projects?.length || 0
          } projects? This will replace all existing data.`
        )
      ) {
        await db.importData(data);
        alert(
          "Data imported successfully! Please refresh the page to see changes."
        );
        window.location.reload();
      }
    } catch (error) {
      console.error("Import failed:", error);
      alert(`Failed to import data: ${error.message}`);
    }

    // Reset file input
    event.target.value = "";
  };

  const handleClearAllData = async () => {
    try {
      await db.clearAllData();
      setShowClearConfirm(false);
      alert("All data cleared successfully! Please refresh the page.");
      window.location.reload();
    } catch (error) {
      console.error("Clear data failed:", error);
      alert("Failed to clear data. Please try again.");
    }
  };

  const handleCleanupData = async () => {
    try {
      // Clean up orphaned data
      const projects = await db.getProjects();
      const tasks = await db.getTasks();
      const sessions = await db.getAll("timer_sessions");

      let cleanedTasks = 0;
      let cleanedSessions = 0;

      // Remove tasks with invalid project references
      for (const task of tasks) {
        if (task.projectId && !projects.find((p) => p.id === task.projectId)) {
          await db.delete("tasks", task.id);
          cleanedTasks++;
        }
      }

      // Remove sessions with invalid task references
      for (const session of sessions) {
        if (session.taskId && !tasks.find((t) => t.id === session.taskId)) {
          await db.delete("timer_sessions", session.id);
          cleanedSessions++;
        }
      }

      alert(
        `Cleanup completed! Removed ${cleanedTasks} orphaned tasks and ${cleanedSessions} orphaned sessions.`
      );
    } catch (error) {
      console.error("Cleanup failed:", error);
      alert("Failed to cleanup data. Please try again.");
    }
  };

  const handleTimeFormatChange = (newFormat) => {
    setTimeFormat(newFormat);
    localStorage.setItem("timeFormat", newFormat);
    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent("timeFormatChanged", {
        detail: { format: newFormat },
      })
    );
    alert(
      `Time and date format changed to ${
        newFormat === "24h"
          ? "24-hour (EU) - 14:30, 15/12/2023"
          : "12-hour (USA) - 2:30 PM, 12/15/2023"
      }`
    );
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split("\n");
    const headers = lines[0].split(",").map((h) => h.replace(/"/g, ""));

    const projects = [];
    const tasks = [];
    const projectMap = new Map();

    // Process data rows
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(",").map((v) => v.replace(/"/g, ""));
        const row = {};

        headers.forEach((header, index) => {
          row[header.toLowerCase().replace(/\s+/g, "")] = values[index] || "";
        });

        // Create project if not exists
        const projectName = row.project || "Imported Project";
        if (!projectMap.has(projectName)) {
          const projectId = `imported-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          projects.push({
            id: projectId,
            name: projectName,
            color: "#97feed",
            createdAt: new Date().toISOString(),
          });
          projectMap.set(projectName, projectId);
        }

        // Create task
        tasks.push({
          id: `imported-task-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          projectId: projectMap.get(projectName),
          title: row.tasktitle || "Imported Task",
          description: row.description || "",
          category: row.category || "",
          status: row.status || "todo",
          timeTracked: parseInt(row.timetrackedseconds) || 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    }

    return { projects, tasks, sessions: [] };
  };

  return (
    <div className="data-export">
      <div className="export-info">
        <small>Data saved locally</small>
      </div>

      <button
        onClick={() => setShowDataModal(true)}
        className="data-management-btn"
        title="Open data management options"
      >
        Data Management
      </button>

      {showDataModal && (
        <div className="data-management-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Data Management</h3>
              <button
                onClick={() => setShowDataModal(false)}
                className="close-btn"
              >
                Close
              </button>
            </div>

            <div className="modal-body">
              <div className="settings-section">
                <h4>Settings</h4>
                <div className="settings-controls">
                  <label htmlFor="time-format">Time & Date Format:</label>
                  <select
                    id="time-format"
                    value={timeFormat}
                    onChange={(e) => handleTimeFormatChange(e.target.value)}
                    className="time-format-selector"
                  >
                    <option value="24h">
                      24-hour (EU) - 14:30, 15/12/2023
                    </option>
                    <option value="12h">
                      12-hour (USA) - 2:30 PM, 12/15/2023
                    </option>
                  </select>
                </div>
              </div>

              <div className="export-section">
                <h4>Export Data</h4>
                <div className="export-controls">
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="format-selector"
                  >
                    <option value="csv">CSV Format</option>
                    <option value="json">JSON Format</option>
                  </select>
                  <button onClick={handleExport} className="export-btn">
                    Export Data
                  </button>
                </div>
              </div>

              <div className="import-section">
                <h4>Import Data</h4>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleImport}
                  className="file-input"
                />
                <small>Select CSV or JSON file to import</small>
              </div>

              <div className="maintenance-section">
                <h4>Data Maintenance</h4>
                <div className="maintenance-controls">
                  <button onClick={handleCleanupData} className="cleanup-btn">
                    Cleanup Orphaned Data
                  </button>
                  <button
                    onClick={() => {
                      setShowDataModal(false);
                      setShowClearConfirm(true);
                    }}
                    className="clear-data-btn"
                  >
                    Clear All Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="clear-confirm-modal">
          <div className="modal-content">
            <h3>Clear All Data</h3>
            <p>
              This will permanently delete all your tasks, projects, and timer
              sessions. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAllData}
                className="confirm-clear-btn"
              >
                Clear All Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataExport;
